import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, IconButton, Input, Typography } from '@mui/joy';
import { MagnifyingGlass, ArrowsClockwise, Columns, UploadSimple } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { KanbanBoardView } from '../../../components/kanban';
import type { KanbanColumnModel, KanbanMoveArgs } from '../../../components/kanban';
import { LIFECYCLE_META } from '../../../components/admin/LifecycleStageBar';
import { AdminBoardsAPI, AdminCrmAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { Lead } from '../../../types/admin';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { CrmTemplatesModal } from './CrmTemplatesModal';
import { LeadOutreachIndicator } from './LeadOutreachIndicator';
import { CreateRecordModal } from '../../../components/admin/CreateRecordModal';
import { BoardColumnsModal } from '../../../components/admin/BoardColumnsModal';
// TEMPORARY — the one-off ApexCRM migration. Delete this import, the
// `importing` state, the Import button and the <ImportApexCrmModal> below
// once the migration has been run; the service and its management command
// stay behind as the record of how it was done.
import { ImportApexCrmModal } from '../../../components/admin/ImportApexCrmModal';
import { CONTACT_TIME_ZONES } from './contactTimeZoneUtils';
import { ContactTimeZone } from './ContactTimeZone';

const LEAD_PAGE_SIZE = 200;

/**
 * Apex's own sales pipeline.
 *
 * The board, columns and drag behaviour all come from the shared
 * `components/kanban` module — this file supplies only what is specific to a
 * lead: how a card looks, and what moving one means.
 */
export const AdminCrmPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openLeadId = searchParams.get('contact');
  const setOpenLeadId = (id: string | null) => setSearchParams(previous => {
    const next = new URLSearchParams(previous);
    if (id) next.set('contact', id);
    else next.delete('contact');
    return next;
  });
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingColumns, setEditingColumns] = useState(false);
  const [importing, setImporting] = useState(false); // TEMPORARY — see import above

  // Debounced so typing does not fire a request per keystroke.
  React.useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const boardQuery = useQuery({
    queryKey: ['admin', 'boards', 'crm'],
    queryFn: () => AdminBoardsAPI.list('crm'),
    refetchInterval: 60_000,
  });

  const board = boardQuery.data?.results.find((b) => b.is_default) ?? boardQuery.data?.results[0];

  const leadsQuery = useQuery({
    queryKey: ['admin', 'leads', { search, board: board?.id }],
    // page_size is honoured by the admin paginator (max 1000). A board has to
    // show whole columns, so the default 20 would silently truncate it.
    queryFn: () =>
      AdminCrmAPI.listLeads({
        search: search || undefined,
        board: board?.id,
        page_size: LEAD_PAGE_SIZE,
      }),
    enabled: board !== undefined,
    placeholderData: keepPreviousData,
    // Reflect outreach recorded by another AE while the board is open.
    refetchInterval: 60_000,
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, boardStatus, position }: { id: string; boardStatus: number; position: number }) =>
      AdminCrmAPI.moveLead(id, boardStatus, position),
    // Refetch rather than patching the cache by hand: the server reindexes the
    // whole column and may move the lifecycle stage too, so its answer is the
    // only reliable picture of what the board should now look like.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) =>
      AdminCrmAPI.createLead({
        ...values,
        source: 'manual',
        // Drop it in the first column so it appears on the board rather than
        // only in search results.
        board_status: [...(board?.statuses ?? [])].sort((a, b) => a.order - b.order)[0]?.id ?? null,
      } as never),
    onSuccess: (lead) => {
      setCreating(false);
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] });
      setOpenLeadId(lead.id);
    },
    onError: (error: unknown) => {
      const data = (error as { response?: { data?: Record<string, string[]> } })?.response?.data;
      // Surface the server's duplicate-email message rather than a generic one.
      setCreateError(data?.email?.[0] ?? 'That lead could not be created.');
    },
  });

  const columns: KanbanColumnModel[] = useMemo(
    () =>
      (board?.statuses ?? []).map((status) => ({
        id: status.id,
        name: status.name,
        order: status.order,
        color: status.color,
        is_done: status.is_done,
        wip_limit: status.wip_limit,
      })),
    [board]
  );

  const leads = leadsQuery.data?.results ?? [];

  const handleMove = async ({ card, toColumnId, toIndex }: KanbanMoveArgs<Lead>) => {
    setMoveError(null);
    await moveMutation.mutateAsync({ id: card.id, boardStatus: toColumnId, position: toIndex });
  };

  const renderCard = (lead: Lead) => {
    const column = board?.statuses.find(status => status.id === lead.board_status);
    const stage = LIFECYCLE_META[column?.lifecycle_stage as Lead['lifecycle_stage']];
    return (
      <Box sx={{ display: 'grid', gap: 0.5 }}>
        <Typography level="title-sm" noWrap>
          {lead.company_name || lead.display_name}
        </Typography>
        {lead.company_name && (lead.first_name || lead.last_name) && (
          <Typography level="body-xs" sx={{ color: 'text.secondary' }} noWrap>
            {`${lead.first_name} ${lead.last_name}`.trim()}
            {lead.title ? ` · ${lead.title}` : ''}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
          <Chip size="sm" variant="soft" color={stage?.color ?? 'neutral'}>
            {column?.name ?? 'Unassigned'}
          </Chip>
          {lead.is_customer_inquiry && (
            <Chip size="sm" variant="soft" color="primary">
              Customer
            </Chip>
          )}
          {lead.team_size && (
            <Chip size="sm" variant="outlined" color="neutral">
              {lead.team_size}
            </Chip>
          )}
        </Box>
        <LeadOutreachIndicator lead={lead} now={now} />
        <ContactTimeZone zone={lead.time_zone} source={lead.time_zone_source} now={now} />
        {lead.owner_name && (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
            {lead.owner_name}
          </Typography>
        )}
      </Box>
    );
  };

  const toolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <CollapsibleFilters><Input
        size="sm"
        placeholder="Search name, company or email"
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        startDecorator={<MagnifyingGlass size={16} />}
        sx={{ minWidth: { xs: 0, sm: 260 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
      /></CollapsibleFilters>
      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
        {leadsQuery.data?.count ?? 0} leads
      </Typography>
      {moveError && (
        <Typography level="body-xs" color="danger">
          {moveError}
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Button size="sm" variant="solid" onClick={() => setCreating(true)}>
        New lead
      </Button>
      <Button size="sm" variant="outlined" color="neutral" onClick={() => setTemplatesOpen(true)}>Templates</Button>
      {/* TEMPORARY — remove with the ApexCRM migration. */}
      <Button
        size="sm"
        variant="outlined"
        color="neutral"
        startDecorator={<UploadSimple size={16} />}
        onClick={() => setImporting(true)}
      >
        Import
      </Button>
      <IconButton
        size="sm"
        variant="outlined"
        color="neutral"
        aria-label="Edit columns"
        onClick={() => setEditingColumns(true)}
      >
        <Columns size={16} />
      </IconButton>
      <IconButton
        size="sm"
        variant="outlined"
        color="neutral"
        aria-label="Refresh"
        onClick={() => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] });
          queryClient.invalidateQueries({ queryKey: ['admin', 'boards'] });
        }}
      >
        <ArrowsClockwise size={16} />
      </IconButton>
    </Box>
  );

  return (
    <>
      {templatesOpen && <CrmTemplatesModal onClose={() => setTemplatesOpen(false)} />}
      <LeadDetailDrawer key={openLeadId} leadId={openLeadId} onClose={() => setOpenLeadId(null)} />
      {/* TEMPORARY — remove with the ApexCRM migration. */}
      <ImportApexCrmModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })}
      />
      <BoardColumnsModal
        open={editingColumns}
        onClose={() => setEditingColumns(false)}
        boardId={board?.id ?? null}
        kind="crm"
      />
      <CreateRecordModal
        open={creating}
        onClose={() => {
          setCreating(false);
          setCreateError(null);
        }}
        title="New lead"
        submitLabel="Add lead"
        isSubmitting={createMutation.isPending}
        error={createError}
        onSubmit={(values) => createMutation.mutate(values)}
        fields={[
          { name: 'company_name', label: 'Company', required: true },
          { name: 'first_name', label: 'First name' },
          { name: 'last_name', label: 'Last name' },
          { name: 'email', label: 'Email' },
          { name: 'phone', label: 'Phone' },
          { name: 'time_zone', label: 'Time zone', type: 'autocomplete', options: CONTACT_TIME_ZONES, placeholder: 'Unknown — select a time zone' },
          { name: 'title', label: 'Title' },
        ]}
      />
      <KanbanBoardView<Lead>
      boardScope="admin-crm"
      columns={columns}
      cards={leads}
      getCardId={(lead) => lead.id}
      getColumnId={(lead) => lead.board_status}
      getPosition={(lead) => lead.position}
      renderCard={renderCard}
      onCardClick={(lead) => setOpenLeadId(lead.id)}
      onMove={handleMove}
      onMoveError={(error) =>
        setMoveError(getApiErrorMessage(error, 'That move could not be saved.'))
      }
      boardColor={board?.color}
      isLoading={boardQuery.isLoading || leadsQuery.isLoading}
      isFetching={leadsQuery.isFetching || moveMutation.isPending}
      error={(boardQuery.error ?? leadsQuery.error) as Error | null}
      onRetry={() => {
        boardQuery.refetch();
        leadsQuery.refetch();
      }}
      toolbar={toolbar}
      emptyLabel="No leads in this stage"
      emptyColumnsSlot={
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          The Apex Sales board has no columns yet.
        </Typography>
      }
      />
    </>
  );
};

export default AdminCrmPanel;
